<template>
  <div id="main-content">
    <div class="content-page-banner">
      <div class="banner-content">
        <h1>{{ $route.meta.title }}</h1>
        <div class="underline"></div>
        <section class="content fr-view">
          <ApiLoading v-if="apiStatus.refData.loading" :apiError="apiStatus.refData.fail" />
          <template v-else>
            <div
              v-if="referenceData && referenceData.changeMyExcessDescriptionText.informationText && !apiStatus.refData.loading && !apiStatus.refData.fail"
              v-html="referenceData.changeMyExcessDescriptionText.informationText"
            />
          </template>
        </section>
      </div>
    </div>
    <Alert v-if="submitPolicyError" alertType="error" :message="ErrorStrings.submitPolicyError" />
    <Alert
      v-else-if="!isApiLoading && referenceData && referenceData.noExcessOptionsErrorText.informationText && excessChangeNotAllowed"
      alertType="error"
      :message="referenceData.noExcessOptionsErrorText.informationText"
    />
    <Alert v-else-if="isApiFail" alertType="error" />
    <div id="multi-step-form">
      <MultiStepFormNavigation :currentPage="currentPage" :links="navLinks" @linkClicked="updateStep" />
      <ApiLoading v-if="isApiLoading || isApiFail" :apiError="isApiFail" />
      <template v-else>
        <div class="form-contents">
          <keep-alive>
            <component :is="currentPageView" :referenceData="referenceData" />
          </keep-alive>
          <div class="form-page-nav">
            <section class="form-buttons" v-if="!apiStatus.loading && !apiStatus.fail">
              <button id="back-button" v-if="currentPage !== steps.CHANGE_EXCESS.pageNumber" class="secondary" @click.prevent="handleBackButton">Back</button>
              <button id="next-button" @click.prevent="handleNextButton" :disabled="isNextButtonDisabled">
                {{ getNextBtnLabel }}
              </button>
            </section>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { format } from 'date-fns';
import { ProductTypeCodes } from '@general/js/enums';
import { Alert, ApiLoading, MultiStepFormNavigation } from '@/components';
import { isPastDate, reverseFormatFormDate, SubmitPolicyError, SubmitPolicyInProcessError, SubmitPolicyTimeoutError} from '@general/js/utils';
import { ErrorStrings } from '@general/js/constants';
import { sortByObjectProperty, getQueryString, replaceTokenInString, isEmptyString } from '@/utils';
import ChangeExcessServices from '@/services/MyCover/ChangeExcess';
import { EXCESS_CODE_MAPPING } from '@/constants/ProductExcessConstants';
import CommonServices from '@/services/Common';
import { CoverCompositionCRMCodeMapping } from '@/enums';
import { CHANGE_EXCESS_PREMIUM_IMPACT } from '@/router/routeNames';
import { setNavigationState } from '@/router/navigationState';
import { ChangeExcessStep, ReviewAndSubmit } from './ChangeExcessSteps';
import ChangeExcessMixins from './ChangeExcessMixins';

export default {
  name: 'ChangeExcess',

  components: {
    Alert,
    ApiLoading,
    MultiStepFormNavigation,
    ChangeExcessStep,
    ReviewAndSubmit,
  },

  mixins: [ChangeExcessMixins],

  data() {
    return {
      apiStatus: {
        refData: {
          loading: false,
          fail: false,
        },
        coverDetails: {
          loading: false,
          fail: false,
        },
        newPremiumOnNewExcess: {
          loading: false,
          fail: false,
        },
        submitNewExcess: {
          loading: false,
          fail: false,
        },
      },
      steps: {
        CHANGE_EXCESS: {
          pageNumber: 1,
          title: 'Change excess',
          component: 'ChangeExcessStep',
          isInvalid: false,
        },
        REVIEW_AND_SUBMIT: {
          pageNumber: 2,
          title: 'Review and submit',
          component: 'ReviewAndSubmit',
          isInvalid: true,
        },
      },
      currentPageView: 'ChangeExcessStep',
      referenceData: null,
      isClosedHospitalProduct: false,
      submitPolicyError: false,
      ErrorStrings,
    };
  },

  created() {
    this.fetchReferenceData();
    // Only call retrieve cover details on created hook if it's the first page to prevent errors when reloading from review and submit step
    if (this.currentPage !== this.steps.REVIEW_AND_SUBMIT.pageNumber) {
      this.retrieveCoverDetails();
    }
  },

  beforeMount() {
    let currentStep = Number(getQueryString('step'));
    if (currentStep) {
      currentStep = Number.isNaN(currentStep) ? 1 : currentStep;
      this.updateStep(currentStep);
    } else {
      this.updateStep(1);
    }
  },

  mounted() {
    window.addEventListener('popstate', this.navigationCallback, false);
  },

  beforeUnmount() {
    window.removeEventListener('popstate', this.navigationCallback);
  },

  computed: {
    navLinks() {
      return [
        { linkTitle: this.steps.CHANGE_EXCESS.title, pages: [this.steps.CHANGE_EXCESS.pageNumber], disabled: false },
        { linkTitle: this.steps.REVIEW_AND_SUBMIT.title, pages: [this.steps.REVIEW_AND_SUBMIT.pageNumber], disabled: this.steps.CHANGE_EXCESS.isInvalid },
      ];
    },

    isApiLoading() {
      return this.apiStatus.refData.loading || this.apiStatus.coverDetails.loading || this.apiStatus.newPremiumOnNewExcess.loading || this.apiStatus.submitNewExcess.loading;
    },

    isApiFail() {
      return this.apiStatus.refData.fail || this.apiStatus.coverDetails.fail || this.apiStatus.newPremiumOnNewExcess.fail || this.apiStatus.submitNewExcess.fail;
    },

    isNextButtonDisabled() {
      return this.navLinks.filter((obj) => {
        return obj.pages[0] === this.currentPage + 1;
      })[0]?.disabled;
    },

    getNextBtnLabel() {
      if (this.currentPage !== this.steps.REVIEW_AND_SUBMIT.pageNumber) {
        return 'Review excess change';
      }
      return 'Update excess';
    },
  },

  watch: {
    '$route.query.step': {
      handler(val) {
        if (val !== undefined) this.updateStep(Number(val));
      },
    },
    isApiFail(newValue) {
      // Do not allow to get into review and submit step if API fails and in current page is Change excess step
      if (!newValue && this.currentPage === this.steps.CHANGE_EXCESS.pageNumber) {
        this.steps.CHANGE_EXCESS.isInvalid = true;
      }
    },
  },

  methods: {
    async navigationCallback() {
      // Update the current page using the step query
      const page = this.$route.query.step ? parseInt(this.$route.query.step, 10) : 1;

      if (page === this.steps.REVIEW_AND_SUBMIT.pageNumber) {
        // Wait for cover details call to complete if redirected directly to 2nd step
        await this.retrieveCoverDetails();
      }

      this.updateStep(page);
    },

    updateStep(newPageNumber) {
      switch (newPageNumber) {
        case this.steps.CHANGE_EXCESS.pageNumber:
          this.updateCurrentPage(this.steps.CHANGE_EXCESS.pageNumber);
          this.updateCurrentPageView(this.steps.CHANGE_EXCESS.component);
          break;
        case this.steps.REVIEW_AND_SUBMIT.pageNumber:
          this.fetchNewPremiumOnNewExcess(this.newExcess);
          this.updateCurrentPage(this.steps.REVIEW_AND_SUBMIT.pageNumber);
          this.updateCurrentPageView(this.steps.REVIEW_AND_SUBMIT.component);
          break;

        default:
          break;
      }
    },

    updateCurrentPageView(targetPageName) {
      this.currentPageView = targetPageName;
    },

    handleBackButton() {
      this.updateStep(this.currentPage - 1);
    },

    handleNextButton() {
      if (this.currentPage === this.steps.REVIEW_AND_SUBMIT.pageNumber) {
        this.submitNewExcess();
      } else {
        this.updateStep(this.currentPage + 1);
      }
    },

    checkIfExcessIsSupported(productExcess) {
      const productCompositionCode = CoverCompositionCRMCodeMapping.find((productCompositionItem) => {
        return productCompositionItem.value === this.composition;
      })?.code;

      const productEligibilityExcessCode = EXCESS_CODE_MAPPING.find((productEligibilityExcessItem) => {
        return productEligibilityExcessItem.value.includes(this.eligibility);
      })?.code;

      // Set the common excess check regardless if it's an open or closed product
      const commonExcessCheck =
        productExcess.excess !== this.currentExcess && // If the product excess is not equal to the current excess
        productExcess.composition.split(',').includes(productCompositionCode) && // Check if current composition code is allowed with the excess option
        productExcess.eligibilitySegment.split(',').includes(productEligibilityExcessCode); // Check if current eligibility code is allowed with the excess option

      if (this.isClosedHospitalProduct) {
        return commonExcessCheck; // Present excess level option with just the base checks
      }

      // If policy's product is an open product, check if it the excess is closed or not
      const isClosedExcessOption = isEmptyString(productExcess.endDate) ? false : isPastDate(reverseFormatFormDate(productExcess.endDate));
      // Present excess level option when all the base checks are satisfied and if it's not closed
      return commonExcessCheck && !isClosedExcessOption;
    },

    async fetchReferenceData() {
      this.apiStatus.refData.loading = true;
      this.apiStatus.refData.fail = false;

      try {
        this.referenceData = await ChangeExcessServices.getReferenceData();
      } catch (error) {
        this.apiStatus.refData.fail = true;
        logger.log(error.message ?? error);
      } finally {
        this.apiStatus.refData.loading = false;
      }
    },

    async retrieveCoverDetails() {
      this.apiStatus.coverDetails.loading = true;
      this.apiStatus.coverDetails.fail = false;

      try {
        const resCoverDetails = await ChangeExcessServices.getCoverDetails(this.memberNumber);

        if (resCoverDetails && resCoverDetails.membership && resCoverDetails.eligibility && resCoverDetails.products) {
          this.updateExcessData(resCoverDetails);
          // Set first step as invalid if excess change is not allowed
          if (this.excessChangeNotAllowed) {
            this.steps.CHANGE_EXCESS.isInvalid = true;
          }
        }
      } catch (error) {
        this.apiStatus.coverDetails.fail = true;
        logger.log(error.message ?? error);
      } finally {
        this.apiStatus.coverDetails.loading = false;
      }
    },

    updateExcessData(coverDetails) {
      const coverDetailsMembership = coverDetails.membership;

      // Save current excess in vuex
      this.updateCurrentExcess(coverDetailsMembership.excess);
      // Save composition
      this.updateComposition(coverDetailsMembership.composition);
      // Save current eligibility
      this.updateEligibility(coverDetails.eligibility.eligibilityType);

      // Save current ongoing premium and frequency
      this.updateCurrentPremiumAndFrequency({
        currentPremium: coverDetailsMembership.premiumInformation.currentOngoingPremium,
        currentFrequency: coverDetailsMembership.frequency,
      });

      const hospitalProduct = coverDetails.products.find((product) => {
        return product.productTypeCode === ProductTypeCodes.PACKAGE || product.productTypeCode === ProductTypeCodes.HOSPITAL_ONLY; // Get only the hospital product with the productTypeCode
      });

      if (hospitalProduct) {
        // Check if closed product
        if (hospitalProduct.validToDate) {
          this.isClosedHospitalProduct = isPastDate(reverseFormatFormDate(hospitalProduct.validToDate));
        }

        if (hospitalProduct.productExcessType) {
          // Save product excess type in vuex
          this.updateExcessType(hospitalProduct.productExcessType);
        }
        if (hospitalProduct.productExcess && Array.isArray(hospitalProduct.productExcess)) {
          const unsortedExcessOptions = [];

          hospitalProduct.productExcess.forEach((productExcessItem) => {
            if (this.checkIfExcessIsSupported(productExcessItem)) {
              unsortedExcessOptions.push({
                value: productExcessItem.excess.toString(),
                name: this.formatAsCurrency(productExcessItem.excess),
              });
            }
          });

          // Sort from highest to lowest excess
          const formattedExcessOptions = sortByObjectProperty(unsortedExcessOptions, 'value', null, 'descending');
          this.updateExcessOptions(formattedExcessOptions);

          if (formattedExcessOptions.length >= 1) {
            // Set initial selected nominated excess
            this.updateNewExcess(formattedExcessOptions[0].value);
          }
        }
      }
    },

    async fetchNewPremiumOnNewExcess(newExcess) {
      this.apiStatus.newPremiumOnNewExcess.loading = true;
      this.apiStatus.newPremiumOnNewExcess.fail = false;

      try {
        const resNewPremium = await ChangeExcessServices.getPremiumOnNewExcess(this.memberNumber, newExcess);
        this.updateNewPremiumAndFrequency({ newPremium: resNewPremium?.newPremium, newFrequency: this.currentPremiumAndFrequency?.frequency });
      } catch (error) {
        this.apiStatus.newPremiumOnNewExcess.fail = true;
        logger.log(error.message ?? error);
      } finally {
        this.apiStatus.newPremiumOnNewExcess.loading = false;
      }
    },

    async submitNewExcess() {
      this.apiStatus.submitNewExcess.loading = true;
      this.apiStatus.submitNewExcess.fail = false;
      this.submitPolicyError = false;
      this.updateSubmitPolicyInProcess(false);

      try {
        const payload = {
          amount: {
            value: this.newExcess,
          },
        };

        // Fetch reference data for premium impact and submit new excess
        const responses = await Promise.all([ChangeExcessServices.getPremiumImpactReferenceData(), ChangeExcessServices.submitNewExcess(this.memberNumber, payload)]);

        const resPremiumImpactRefData = responses[0];
        const resSubmitNewExcess = responses[1];

        const replacedNewExcessText = replaceTokenInString(
          resPremiumImpactRefData.updatedProductInformationText.informationText,
          '#{newExcess}',
          this.convertToCurrencyString(parseInt(this.newExcess, 10), 0),
        );
        const replacedExcessTypeText = replaceTokenInString(replacedNewExcessText, '#{excessType}', this.excessTypeText);
        const formattedProductInformationText = replaceTokenInString(replacedExcessTypeText, '#{nextPaymentDate}', format(new Date(), 'dd MMMM yyyy'));

        const refDataPremiumImpact = {
          premiumImpactInformationText: resPremiumImpactRefData.premiumImpactDescriptionText,
          updatedProductInformationText: formattedProductInformationText,
          paymentAdjustmentInformationText: resPremiumImpactRefData.adjustedPaymentInformationText,
        };

        // Pass refData and new excess as props to premium impact component
        setNavigationState({ refData: refDataPremiumImpact, newPremium: resSubmitNewExcess.newPremium });
        this.$router.push({ name: CHANGE_EXCESS_PREMIUM_IMPACT });
      } catch (error) {
        logger.error(error);
        if (error instanceof SubmitPolicyError) {
          this.submitPolicyError = true;
        }
        if (error instanceof SubmitPolicyInProcessError || error instanceof SubmitPolicyTimeoutError) {
          this.updateSubmitPolicyInProcess(true);

          // Raise work item with payload only for timeout error
          if (error instanceof SubmitPolicyTimeoutError) {
            const payload = {
              amount: {
                value: this.newExcess,
              },
            };

            const request = {
              functionName: "submit-new-excess",
              workItemCategory: "OTHERS",
              title: `Submission Timeout for ${this.$route?.params?.policyNumber} in Member Portal`,
              payload,
            };

            await CommonServices.raiseWorkItem(this.$route?.params?.policyNumber, request);
          }

          // Redirect to success page
          setNavigationState({ flowName: 'excess change' });
          this.$router.push({ name: CHANGE_EXCESS_PREMIUM_IMPACT });

        } else {
          this.apiStatus.submitNewExcess.fail = true;
        }
      } finally {
        this.apiStatus.submitNewExcess.loading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.content-page-banner {
  margin-bottom: 2rem;
}

:deep(.header-container) {
  margin-bottom: 2rem;
  h3 {
    margin-bottom: 0;
  }
}
</style>
